import React, { useEffect, useMemo } from 'react';

import {
  isUrlNamesMatch,
  SiteConfigManager,
  useApplicationState,
  useSportsbookState,
  useSportsbookStateUpdate,
} from '@atlas/core';
import { SportLink } from '@atlas/routing';
import { sportsbookRoutes } from '@atlas/routing';
import { ChipsSportItemsSkeleton, SportTypeChipsItem } from '@atlas/ui';

import { useSimplifiedSportsbook } from 'common/SimplifiedSportsbook/SimplifiedSportsbookContext';
import useOnScreen from 'common/utils/useOnScreen';

const SportsTypeChipsList = ({ className }) => {
  const { authenticated } = useApplicationState();
  const { initialized, loading, selectedSportUrlName, sports } = useSportsbookState();
  const sportsbookStateUpdate = useSportsbookStateUpdate();
  const { setSportName, sportName: simplifiedSportName } = useSimplifiedSportsbook();
  const activeSport = React.useRef(null);
  const isVisible = useOnScreen(activeSport);
  const isVisibleRef = React.useRef(isVisible);

  const isSimplifiedView = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.mobileGridViewType'),
    [],
  );

  const isEnabledFavoritesPage = SiteConfigManager.getConfig(
    'config.config.sportsbook.enableFavouritesInSimplified',
  );

  const filterOptions = useMemo(() => {
    if (isEnabledFavoritesPage && authenticated) {
      return [
        {
          customIcon: 'fav',
          id: sportsbookRoutes.favorites,
          name: '',
          urlName: sportsbookRoutes.favorites,
        },
        ...sports,
      ];
    }

    return sports;
  }, [isEnabledFavoritesPage, authenticated, sports]);

  useEffect(() => {
    if (isSimplifiedView === 'simplified') {
      setSportName(selectedSportUrlName);
    }
  }, [isSimplifiedView, selectedSportUrlName, setSportName]);

  useEffect(() => {
    if (initialized && activeSport.current && !isVisibleRef.current) {
      activeSport?.current?.scrollIntoView({
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [initialized]);

  if (!initialized && loading) {
    return (
      <div className="sports-list sport-type-list sport-type-skeleton">
        <ChipsSportItemsSkeleton />
      </div>
    );
  }

  if (sports.length === 0) {
    return null;
  }

  return (
    <div className="sports-list sport-type-list">
      {filterOptions.map(sport => {
        const { customIcon, id, name, urlName } = sport;
        const isActive = isUrlNamesMatch(
          urlName,
          isSimplifiedView ? simplifiedSportName : selectedSportUrlName,
        );

        const handleSelectSport = sportUrlName => {
          sportsbookStateUpdate({ selectedSportUrlName: sportUrlName });
        };

        const onClick = e => {
          if (isActive) {
            e.preventDefault();
          }
        };

        return (
          <div className="sports-item-wrapper" key={id} ref={isActive ? activeSport : null}>
            <SportLink key={id} onClick={onClick} sportUrlName={urlName}>
              <SportTypeChipsItem
                active={isActive}
                className={className}
                icon={customIcon || `sportIcon-${id}`}
                label={name}
                onClick={() => handleSelectSport(selectedSportUrlName)}
                sportUrlName={urlName}
              />
            </SportLink>
          </div>
        );
      })}
    </div>
  );
};

export default SportsTypeChipsList;
